import {
  AutoAwesome,
  ExpandLess,
  ExpandMore,
  Person,
  Send,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import { FileAttachment } from 'further-types/lp/ai-agent';
import { UploadType } from 'further-types/files';
import { Column, Row } from 'further-ui/components/Layout';
import useFileUpload from 'hooks/ui/useFileUpload';
import React, {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export type AiAgentChatRef = {
  addMessage: (message: {
    role: 'user' | 'assistant';
    content: string;
  }) => void;
};

type ChatMessageData<TProposal> = {
  role: 'user' | 'assistant';
  content: string;
  proposal?: TProposal;
  fileName?: string;
};

type ProposalCardRenderProps<TProposal> = {
  proposal: TProposal;
  vehicleId: string;
  onApprove: (proposal: TProposal) => void;
  onReject: () => void;
  isExecuting: boolean;
};

const renderMarkdown = (text: string): ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

type ChatMessageProps<TProposal> = {
  message: ChatMessageData<TProposal>;
  vehicleId: string;
  onApproveProposal: (proposal: TProposal) => void;
  onRejectProposal: () => void;
  isExecuting: boolean;
  renderProposalCard: (props: ProposalCardRenderProps<TProposal>) => ReactNode;
};

function ChatMessage<TProposal>({
  message,
  vehicleId,
  onApproveProposal,
  onRejectProposal,
  isExecuting,
  renderProposalCard,
}: ChatMessageProps<TProposal>) {
  const isUser = message.role === 'user';

  return (
    <Row spacing="md">
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: isUser ? 'secondary.main' : 'background.paper',
          border: isUser ? 'none' : 1,
          borderColor: 'divider',
        }}
      >
        {isUser ? (
          <Person fontSize="small" sx={{ color: 'common.white' }} />
        ) : (
          <img src="/favicon.ico" alt="AI" width={20} height={20} />
        )}
      </Avatar>
      <Column spacing="xxs">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 'bold' }}
        >
          {isUser ? 'You' : 'Further AI Assistant'}
        </Typography>
        {message.fileName && (
          <Box
            sx={{
              mb: 1,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              display: 'inline-block',
            }}
          >
            <Typography variant="body2">
              Uploaded: {message.fileName}
            </Typography>
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {isUser ? message.content : renderMarkdown(message.content)}
        </Typography>
        {message.proposal !== undefined &&
          renderProposalCard({
            proposal: message.proposal,
            vehicleId,
            onApprove: onApproveProposal,
            onReject: onRejectProposal,
            isExecuting,
          })}
      </Column>
    </Row>
  );
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getMediaType = (file: File): FileAttachment['mediaType'] | null => {
  const { type } = file;
  if (
    type === 'image/jpeg' ||
    type === 'image/png' ||
    type === 'image/gif' ||
    type === 'image/webp' ||
    type === 'application/pdf'
  ) {
    return type;
  }
  return null;
};

type Props<TProposal> = {
  vehicleId: string;
  description?: string;
  dropzoneMessage?: string;
  inputPlaceholder?: string;
  isExecuting: boolean;
  onChat: (params: {
    vehicleId: string;
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    file?: FileAttachment;
  }) => Promise<{ message: string; proposal?: TProposal | null }>;
  onApproveProposal: (proposal: TProposal) => void;
  onFileUploaded?: (result: { path: string; name: string }) => void;
  renderProposalCard: (props: ProposalCardRenderProps<TProposal>) => ReactNode;
};

function AiAgentChatInner<TProposal>(
  {
    vehicleId,
    description,
    dropzoneMessage,
    inputPlaceholder = 'Ask the AI assistant...',
    isExecuting,
    onChat,
    onApproveProposal,
    onFileUploaded,
    renderProposalCard,
  }: Props<TProposal>,
  ref: ForwardedRef<AiAgentChatRef>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Array<ChatMessageData<TProposal>>>(
    [],
  );
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const dropzoneKey = useRef(0);
  const { uploadFile } = useFileUpload();

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  }, []);

  const addMessage = useCallback(
    (message: { role: 'user' | 'assistant'; content: string }) => {
      setMessages((prev) => [...prev, message]);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  useImperativeHandle(ref, () => ({ addMessage }), [addMessage]);

  const sendMessage = async (
    text: string,
    file?: FileAttachment,
    fileName?: string,
  ) => {
    if (!text.trim() && !file) return;

    const messageContent =
      text.trim() || (fileName ? `Please process this file: ${fileName}` : '');
    const userMessage: ChatMessageData<TProposal> = {
      role: 'user',
      content: messageContent,
      fileName,
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputText('');
    setIsLoading(true);
    scrollToBottom();

    try {
      const chatMessages = updatedMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const response = await onChat({
        vehicleId,
        messages: chatMessages,
        file,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response.message,
          proposal: response.proposal ?? undefined,
        },
      ]);
      scrollToBottom();
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => sendMessage(inputText);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileDrop = async (files: Array<UploadedFile>) => {
    const file = files[0];
    if (!file?.path) return;

    const rawFile = file as unknown as File;
    const mediaType = getMediaType(rawFile);
    if (!mediaType) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Please upload an image (JPEG, PNG, GIF, WebP) or PDF file.',
        },
      ]);
      return;
    }

    try {
      const [base64, uploadResult] = await Promise.all([
        fileToBase64(rawFile),
        uploadFile(file, UploadType.LpTransactionAttachments),
      ]);

      if (uploadResult.data?.filePath && onFileUploaded) {
        onFileUploaded({
          path: uploadResult.data.filePath,
          name: uploadResult.data.name ?? '',
        });
      }

      dropzoneKey.current += 1;
      sendMessage(
        inputText,
        { base64, mediaType, fileName: rawFile.name },
        rawFile.name,
      );
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Failed to process the file. Please try again.',
        },
      ]);
    }
  };

  const handleRejectProposal = () => {
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: "I'd like to cancel this proposal." },
    ]);
    sendMessage(
      'The user has rejected the proposal. Ask them what they would like to change.',
    );
  };

  return (
    <Card variant="outlined">
      <Row
        spacing="sm"
        centered
        justify="space-between"
        sx={{
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Row spacing="sm" centered>
          <AutoAwesome color="primary" />
          <Typography variant="h6">AI Assistant</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Row>
        <IconButton size="small">
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Row>

      <Collapse in={isOpen}>
        <CardContent sx={{ pt: 4 }}>
          <Column spacing="md">
            <Fade in={messages.length > 0 || isLoading} unmountOnExit>
              {/* Box used here as Fade requires a ref-forwarding child */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  maxHeight: 500,
                  overflowY: 'auto',
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {messages.map((message, index) => (
                  <ChatMessage
                    key={index}
                    message={message}
                    vehicleId={vehicleId}
                    onApproveProposal={onApproveProposal}
                    onRejectProposal={handleRejectProposal}
                    isExecuting={isExecuting}
                    renderProposalCard={renderProposalCard}
                  />
                ))}
                {isLoading && (
                  <Row spacing="md" centered sx={{ mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      AI is thinking...
                    </Typography>
                  </Row>
                )}
              </Box>
            </Fade>

            <Row spacing="sm" sx={{ alignItems: 'stretch' }}>
              <Box sx={{ flex: 1 }}>
                <Dropzone
                  key={dropzoneKey.current}
                  name="ai-agent-file"
                  maxFiles={1}
                  defaultAcceptGroup="filesAndImages"
                  message={dropzoneMessage}
                  onDropFileSync={handleFileDrop}
                  files={[]}
                  onRemove={() => {}}
                />
              </Box>
              <Column spacing="sm" centered justify="center">
                <Divider orientation="vertical" flexItem sx={{ flex: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1, left: '0.25rem', position: 'relative' }}
                >
                  or
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ flex: 1 }} />
              </Column>
              <Row spacing="sm" sx={{ flex: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={inputPlaceholder}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  multiline
                  maxRows={4}
                  disabled={isLoading}
                />
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={isLoading || !inputText.trim()}
                  sx={{ flexShrink: 0 }}
                >
                  <Send />
                </IconButton>
              </Row>
            </Row>
          </Column>
        </CardContent>
      </Collapse>
    </Card>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AiAgentChat = forwardRef(AiAgentChatInner as any) as <T>(
  props: Props<T> & { ref?: React.Ref<AiAgentChatRef> },
) => React.ReactElement;

export default AiAgentChat;
